import { PINNED_ASSET_SLUG } from "@/config/game";
import { useTranslation } from "@repo/shared/i18n";

import { EvolutionsHubCard } from "./EvolutionsHubCard";

interface EvolutionsHubCardsProps {
  activeEvoCard: "overview" | "players" | "builder" | "my-evolutions";
}
export const EvolutionsHubCards = ({
  activeEvoCard,
}: EvolutionsHubCardsProps) => {
  const { t } = useTranslation("evolutions");
  return (
    <div className="grid grid-cols-[repeat(1,minmax(0,30.5rem))] h-full mx-auto gap-4 justify-center px-4 large-desktop:px-16 max-touch-device:gap-2 max-touch-device:px-2 mobile:grid-cols-[repeat(4,minmax(0,22.5rem))]">
      <EvolutionsHubCard
        title={t("hub.overview.title")}
        description={t("hub.overview.description")}
        link="/evolutions"
        icon={{
          text: t("hub.overview.tag"),
          icon: <EvolutionsHubIcon icon="overview" />,
        }}
        imageUrl={
          import.meta.env.VITE_APP_ASSETS_BASE_URL +
          `/${PINNED_ASSET_SLUG}/evolutions-hub/overview.png`
        }
        active={activeEvoCard === "overview"}
      />
      <EvolutionsHubCard
        title={t("hub.players.title")}
        description={t("hub.players.description")}
        link="/evolutions/players"
        icon={{
          text: t("hub.players.tag"),
          icon: <EvolutionsHubIcon icon="players" />,
        }}
        imageUrl={
          import.meta.env.VITE_APP_ASSETS_BASE_URL +
          `/${PINNED_ASSET_SLUG}/evolutions-hub/players.png`
        }
        active={activeEvoCard === "players"}
      />
      <EvolutionsHubCard
        title={t("hub.builder.title")}
        description={t("hub.builder.description")}
        link="/evolutions/builder"
        icon={{
          text: t("hub.builder.tag"),
          icon: <EvolutionsHubIcon icon="builder" />,
        }}
        imageUrl={
          import.meta.env.VITE_APP_ASSETS_BASE_URL +
          `/${PINNED_ASSET_SLUG}/evolutions-hub/builder.png`
        }
        active={activeEvoCard === "builder"}
      />
      <EvolutionsHubCard
        title={t("hub.myEvolutions.title")}
        description={t("hub.myEvolutions.description")}
        link={"/evolutions/my-evolutions"}
        icon={{
          text: t("hub.myEvolutions.tag"),
          icon: <EvolutionsHubIcon icon="my-evolutions" />,
        }}
        imageUrl={
          import.meta.env.VITE_APP_ASSETS_BASE_URL +
          `/${PINNED_ASSET_SLUG}/evolutions-hub/my-evolutions.png`
        }
        active={activeEvoCard === "my-evolutions"}
      />
    </div>
  );
};

const EvolutionsHubIcon = ({ icon }: { icon: string }) => {
  switch (icon) {
    case "players":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.35417 11.0651H5.64583M8.35417 11.0651C8.35417 10.6786 8.35417 10.4854 8.3748 10.3571C8.44132 9.94364 8.45297 9.91758 8.71665 9.59215C8.79839 9.49129 9.10194 9.21948 9.70898 8.67592C10.5397 7.93205 11.0625 6.85126 11.0625 5.64844C11.0625 3.40478 9.24364 1.58594 7 1.58594C4.75634 1.58594 2.9375 3.40478 2.9375 5.64844C2.9375 6.85126 3.46026 7.93205 4.29099 8.67592C4.89804 9.21948 5.2016 9.49129 5.28336 9.59215C5.54704 9.91758 5.55868 9.94364 5.62521 10.3571C5.64583 10.4854 5.64583 10.6786 5.64583 11.0651M8.35417 11.0651C8.35417 11.5713 8.35417 11.8245 8.24529 12.013C8.17401 12.1365 8.07142 12.2391 7.94792 12.3104C7.75936 12.4193 7.50624 12.4193 7 12.4193C6.49376 12.4193 6.24064 12.4193 6.05208 12.3104C5.92858 12.2391 5.826 12.1365 5.75469 12.013C5.64583 11.8245 5.64583 11.5713 5.64583 11.0651"
            stroke="#34D399"
            strokeWidth="0.5"
          />
          <path
            d="M7.42636 5.10156L6.26562 6.72656H7.89062L6.72994 8.35156"
            stroke="#34D399"
            strokeWidth="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "builder":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.625 4.1875L8.47394 8.31327C7.91274 8.87104 7.63216 9.14987 7.28414 9.14987C6.93607 9.14982 6.65555 8.87087 6.09451 8.31299L5.95985 8.17911C5.3983 7.62072 5.11753 7.34149 4.76925 7.34166C4.42097 7.34177 4.1404 7.62117 3.57925 8.17996L1.375 10.375M12.625 4.1875V7.30701M12.625 4.1875H9.48503"
            stroke="#34D399"
            strokeWidth="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "my-evolutions":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.09213 8.85383C1.61404 8.23272 1.375 7.92216 1.375 7C1.375 6.07784 1.61404 5.7673 2.09213 5.14619C3.04673 3.906 4.64769 2.5 7 2.5C9.35232 2.5 10.9532 3.906 11.9079 5.14619C12.3859 5.7673 12.625 6.07784 12.625 7C12.625 7.92216 12.3859 8.23272 11.9079 8.85383C10.9532 10.094 9.35232 11.5 7 11.5C4.64769 11.5 3.04673 10.094 2.09213 8.85383Z"
            stroke="#34D399"
            strokeWidth="0.5"
          />
          <path
            d="M8.6875 7C8.6875 7.93201 7.93201 8.6875 7 8.6875C6.06799 8.6875 5.3125 7.93201 5.3125 7C5.3125 6.06799 6.06799 5.3125 7 5.3125C7.93201 5.3125 8.6875 6.06799 8.6875 7Z"
            stroke="#34D399"
            strokeWidth="0.5"
          />
        </svg>
      );
    case "overview":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.65625 3.90625C1.65625 2.84559 1.65625 2.31526 1.98576 1.98576C2.31526 1.65625 2.84559 1.65625 3.90625 1.65625C4.96691 1.65625 5.49724 1.65625 5.82674 1.98576C6.15625 2.31526 6.15625 2.84559 6.15625 3.90625C6.15625 4.96691 6.15625 5.49724 5.82674 5.82674C5.49724 6.15625 4.96691 6.15625 3.90625 6.15625C2.84559 6.15625 2.31526 6.15625 1.98576 5.82674C1.65625 5.49724 1.65625 4.96691 1.65625 3.90625Z"
            stroke="#34D399"
            strokeWidth="0.5"
          />
          <path
            d="M7.84375 10.0938C7.84375 9.0331 7.84375 8.50278 8.17326 8.17326C8.50278 7.84375 9.0331 7.84375 10.0938 7.84375C11.1544 7.84375 11.6847 7.84375 12.0142 8.17326C12.3437 8.50278 12.3438 9.0331 12.3438 10.0938C12.3438 11.1544 12.3437 11.6847 12.0142 12.0142C11.6847 12.3437 11.1544 12.3438 10.0938 12.3438C9.0331 12.3438 8.50278 12.3437 8.17326 12.0142C7.84375 11.6847 7.84375 11.1544 7.84375 10.0938Z"
            stroke="#34D399"
            strokeWidth="0.5"
          />
          <path
            d="M1.65625 10.0938C1.65625 9.0331 1.65625 8.50278 1.98576 8.17326C2.31526 7.84375 2.84559 7.84375 3.90625 7.84375C4.96691 7.84375 5.49724 7.84375 5.82674 8.17326C6.15625 8.50278 6.15625 9.0331 6.15625 10.0938C6.15625 11.1544 6.15625 11.6847 5.82674 12.0142C5.49724 12.3437 4.96691 12.3438 3.90625 12.3438C2.84559 12.3438 2.31526 12.3437 1.98576 12.0142C1.65625 11.6847 1.65625 11.1544 1.65625 10.0938Z"
            stroke="#34D399"
            strokeWidth="0.5"
          />
          <path
            d="M7.84375 3.90625C7.84375 2.84559 7.84375 2.31526 8.17326 1.98576C8.50278 1.65625 9.0331 1.65625 10.0938 1.65625C11.1544 1.65625 11.6847 1.65625 12.0142 1.98576C12.3437 2.31526 12.3438 2.84559 12.3438 3.90625C12.3438 4.96691 12.3437 5.49724 12.0142 5.82674C11.6847 6.15625 11.1544 6.15625 10.0938 6.15625C9.0331 6.15625 8.50278 6.15625 8.17326 5.82674C7.84375 5.49724 7.84375 4.96691 7.84375 3.90625Z"
            stroke="#34D399"
            strokeWidth="0.5"
          />
        </svg>
      );
  }
};
